"use client";

import type { AppRouteType } from "@layer/utils/lib/routing";
import { getSubRoutes } from "@layer/utils/lib/routing";

import {
	usePathname,
	useSelectedLayoutSegment,
	useSelectedLayoutSegments,
} from "next/navigation";

import {
	createContext,
	type ReactElement,
	type ReactNode,
	useContext,
	useMemo,
} from "react";
import { SidebarInset, SidebarProvider } from "../../../components/sidebar";
import { AppSidebar } from "../app-sidebar";
import { Navbar } from "../navbar";

type ApplicationShellContextType = {
	routes: AppRouteType[];
	subRoutes: AppRouteType[];
	pathname: string;
	segments: string[];
	resolveBreadcrumbTitle?: (segment: string) => string | undefined;
};

const ApplicationShellContext = createContext<
	ApplicationShellContextType | undefined
>(undefined);

type Props = {
	tabKey: string;
	children: ReactNode;
	routes: AppRouteType[];
	components?: ReactElement[];
	sidebarHeaderComponent?: ReactNode;
	isMinimalShell?: boolean;
	alwaysCollapsed?: boolean;
	resolveBreadcrumbTitle?: (segment: string) => string | undefined;
};

export const ApplicationShellProvider = ({
	tabKey,
	children,
	routes,
	components,
	sidebarHeaderComponent,
	isMinimalShell,
	alwaysCollapsed,
	resolveBreadcrumbTitle,
}: Props) => {
	const pathname = usePathname();

	// remove Next.js "__DEFAULT__" segment
	const segments =
		useSelectedLayoutSegments()?.filter((s) => s !== "__DEFAULT__") || [];

	const segment = useSelectedLayoutSegment();

	const subRoutes = useMemo(() => {
		if (!pathname) return [];

		const parentPath = segment ? `/${segment}` : "/";
		return getSubRoutes(parentPath, routes);
	}, [pathname, routes, segment]);

	const value = useMemo(
		() => ({
			routes,
			pathname,
			segments,
			subRoutes,
			resolveBreadcrumbTitle,
		}),
		[routes, pathname, segments, subRoutes, resolveBreadcrumbTitle],
	);

	return (
		<ApplicationShellContext.Provider value={value}>
			<SidebarProvider>
				<div className="flex h-svh overflow-hidden w-full">
					<AppSidebar
						routes={routes}
						pathname={pathname}
						headerComponent={sidebarHeaderComponent}
						isMinimalShell={isMinimalShell}
						alwaysCollapsed={alwaysCollapsed}
					/>
					<SidebarInset>
						<div className="flex min-h-0 flex-1 flex-col overflow-hidden">
							<Navbar
								tabKey={tabKey}
								components={components}
								isMinimalShell={isMinimalShell}
								alwaysCollapsed={alwaysCollapsed}
							/>
							<div className="flex min-h-0 flex-1 flex-col overflow-hidden">
								{children}
							</div>
						</div>
					</SidebarInset>
				</div>
			</SidebarProvider>
		</ApplicationShellContext.Provider>
	);
};

export const useApplicationShell = () => {
	const context = useContext(ApplicationShellContext);

	if (!context) {
		throw new Error(
			"useApplicationShell must be used within an ApplicationShellProvider",
		);
	}

	return context;
};
